import { useContext, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import JsBarcode from 'jsbarcode';

import Button from '../../reusable/button/button.component';
import InputCurrency from '../../reusable/inputs/currency.input';
import InventoryTable from '../../reusable/tables/inventory-table/inventory-table.component';
import Spinner from '../../reusable/spinner/spinner.component';

import { ToastContext } from '../../../contexts/toast.context';
import Client from '../../../tools/client';

import {
  ContentContainer,
  Input,
  MainContainer,
  RowContainer,
  Title
} from '../../../styles/component.styles';

const client = new Client();

const AddInventory = ({ product = null, onComplete = () => {} }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [data, setData] = useState(null);
  const [productId, setProductId] = useState(product ? product.id : 0);
  const [productType, setProductType] = useState(product ? product.ProductType.name : '');
  const [type, setType] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [size, setSize] = useState('');
  const [sizeDescription, setSizeDescription] = useState('');
  const [sku, setSku] = useState('');
  const [addressOne, setAddressOne] = useState('');
  const [addressTwo, setAddressTwo] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [bay, setBay] = useState('');
  const [skuError, setSkuError] = useState('');

  const barcodeRef = useRef(null);
  const { errorToast } = useContext(ToastContext);

  useEffect(() => {
    if (product === null) {
      getProducts();
    }

    if (!sku) {
      const newSku = generateBarcodeValue();
      setSku(newSku);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if(productType === 'Clones') {
      setPrice(25000);
      setType('female');
      setSize('rooted');
      setSizeDescription('1 rooted clone');
    }
  }, [ productType ]);

  useEffect(() => {
    if (sku && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, sku, {
          format: 'CODE128',
          lineColor: '#000',
          width: 2,
          height: 40,
          displayValue: true
        });
        setSkuError('');
      } catch (err) {
        setSkuError('Invalid SKU format. Please use alphanumeric characters only.');
      }
    }
  }, [sku, barcodeRef.current]);

  const getProducts = async () => {
    setLoading(true);
    const res = await client.getProducts();
    setProducts(res.rows);
    setLoading(false);
  };

  const selectProduct = (selected) => {
    const selectedProduct = selected?.value || 0;
    setProductId(selectedProduct);
    const filterProduct = products.find(item => item.id === selectedProduct);
    setData(filterProduct);
    setProductType(filterProduct?.ProductType?.name || '');

    // Reset inventory fields
    setQuantity('');
    setPrice('');
    setType('');
    setSize('');
    setSizeDescription('');
  };

  const generateBarcodeValue = () => `GSI-${Date.now()}`;

  const populateNewSku = () => {
    const newSku = generateBarcodeValue();
    setSku(newSku);
  };

  const selectSize = (e) => {
    if(productType === 'Clones') {
      if(e === 'rooted') {
        setSize(e);
        setSizeDescription('1 rooted clone');
      }
      if(e === 'snips') {
        setSize(e);
        setSizeDescription('3 snips');
      }
      return;
    }
    setSize(e);
  };

  const checkFields = () => {
    if (
      !productId ||
      !type.trim() ||
      quantity === '' ||
      price === '' ||
      !size.trim() ||
      !sizeDescription.trim() ||
      !sku.trim()
    ) {
      return {
        result: false,
        error: 'Please complete all fields to create inventory'
      };
    }
    return { result: true };
  };

  const submitInventory = async () => {
    setLoading(true);
    const check = checkFields();
    if (check.error) {
      errorToast(check.error);
      setLoading(false);
      return;
    }

    const data = {
      productId,
      type,
      quantity,
      price,
      size,
      sizeDescription,
      sku,
      address: {
        addressOne,
        addressTwo,
        city,
        state,
        zipCode
      },
      bay
    };

    await client.createInventory(data);
    await onComplete();
    setLoading(false);
  };

  const addInventoryForm = () => {
    return (
      <ContentContainer>
        <ContentContainer margin='20px'>
          {(!productType || data !== null) && (
            <Select
              value={products.find(p => p.id === productId) ? { value: productId, label: products.find(p => p.id === productId).name } : null}
              onChange={selectProduct}
              options={products.map(p => ({ value: p.id, label: p.name }))}
              placeholder="Select a product..."
              isClearable
            />
          )}
          {data && (
            <>
              <Title>{data.name}</Title>
              <InventoryTable inventories={data.Inventories} product={product} getProduct={getProducts} />
            </>
          )}
        </ContentContainer>

        <Input type="number" min={0} value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Quantity" />
        <InputCurrency value={price} onChange={setPrice} placeholder="Price" />

        {productType === 'Seeds' && (
          <>
            <RowContainer margin="10px 0">
              <select name="inventoryType" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="" disabled> -- Seed Type -- </option>
                <option value="Regular">Regular</option>
                <option value="Feminized">Feminized</option>
              </select>
            </RowContainer>
            <RowContainer margin="10px 0">
              <select name="size" value={size} onChange={(e) => setSize(e.target.value)}>
                <option value="" disabled> -- Pack Size -- </option>
                <option value="Full Pack">Full Pack</option>
                <option value="Half Pack">Half Pack</option>
              </select>
            </RowContainer>
          </>
        )}

        {productType === 'Clones' && (
          <>
            <RowContainer margin="10px 0">
              <select name="inventoryType" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="" disabled> -- Clone Sex -- </option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </RowContainer>
            <RowContainer margin="10px 0">
              <select name="inventorySize" value={size} onChange={(e) => selectSize(e.target.value)}>
                <option value="" disabled> -- Clone Type -- </option>
                <option value="rooted">Rooted</option>
                <option value="snips">Snips</option>
              </select>
            </RowContainer>
          </>
        )}

        <Input type="text" value={sizeDescription} onChange={(e) => setSizeDescription(e.target.value)} placeholder="Size Description" />
        <Input type="text" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU" />
        <button onClick={populateNewSku}>Create New SKU</button>
        {skuError ? (
          <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{skuError}</div>
        ) : (
          <svg ref={barcodeRef} />
        )}

        <Input type="text" value={addressOne} onChange={(e) => setAddressOne(e.target.value)} placeholder="Address One" />
        <Input type="text" value={addressTwo} onChange={(e) => setAddressTwo(e.target.value)} placeholder="Address Two" />
        <Input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
        <Input type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
        <Input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Zip Code" />
        <Input type="text" value={bay} onChange={(e) => setBay(e.target.value)} placeholder="Bay" />

        <RowContainer margin="20px 0">
          <Button onClick={submitInventory}>Add Inventory</Button>
        </RowContainer>
      </ContentContainer>
    );
  };

  return (
    <MainContainer>
      {loading ? <Spinner /> : addInventoryForm()}
    </MainContainer>
  );
};

export default AddInventory;